import type { PointerEvent, RefObject } from "react";

import { useCallback, useEffect, useRef } from "react";

import { useIsLargeOrAbove } from "@/app/_ui/hooks/useBreakpoint";
import { clamp } from "@/app/_ui/lib/math";

import { REDUCED_MOTION_QUERY } from "./ImpactArtifactsGallery.utils";

type GalleryScrollerRef = RefObject<HTMLDivElement | null>;

interface CardMotionState {
  rotation: number;
  rotationVelocity: number;
  velocityX: number;
  velocityY: number;
  x: number;
  y: number;
}

interface CardMotionRect {
  element: HTMLElement;
  rect: DOMRect;
}

const CARD_MOTION_DAMPING = 10;
const CARD_MOTION_INFLUENCE_RADIUS = 1.2;
const CARD_MOTION_LOW_SPEED_POWER = 2.2;
const CARD_MOTION_MIN_VELOCITY_SCALE = 0.08;
const CARD_MOTION_MINIMUM_ACTIVITY = 0.02;
const CARD_MOTION_MAX_VELOCITY_BOOST = 1.7;
const CARD_MOTION_PUSH_STRENGTH = 0.004;
const CARD_MOTION_ROTATION_PUSH_STRENGTH = 0.0004;
const CARD_MOTION_ROTATION_SPRING = 55;
const CARD_MOTION_SPRING = 110;
const CARD_MOTION_VELOCITY_BOOST_START = 700;
const CARD_MOTION_VELOCITY_BOOST_RANGE = 1600;
const CARD_MOTION_VELOCITY_LIMIT = 2600;

export function useImpactArtifactsMotion(scrollerRef: GalleryScrollerRef) {
  const isLargeOrAbove = useIsLargeOrAbove();
  const cardMotionFrameRef = useRef<number | null>(null);
  const cardMotionPreviousTimeRef = useRef<number | null>(null);
  const cardMotionRectsRef = useRef<CardMotionRect[]>([]);
  const cardMotionRectsDirtyRef = useRef(true);
  const cardMotionStatesRef = useRef(new Map<HTMLElement, CardMotionState>());
  const lastPointerRef = useRef({
    hasValue: false,
    time: 0,
    x: 0,
    y: 0,
  });
  const prefersReducedMotionRef = useRef(false);

  const invalidateCardMotionRects = useCallback(() => {
    cardMotionRectsDirtyRef.current = true;
  }, []);

  const getCardMotionRects = useCallback(() => {
    const cachedRects = cardMotionRectsRef.current;

    if (
      !cardMotionRectsDirtyRef.current &&
      cachedRects.length > 0 &&
      cachedRects.every(({ element }) => element.isConnected)
    ) {
      return cachedRects;
    }

    const cardRects = Array.from(
      scrollerRef.current?.querySelectorAll<HTMLElement>("[data-impact-artifact-card]") ?? [],
      element => ({
        element,
        rect: element.getBoundingClientRect(),
      }),
    );

    cardMotionRectsRef.current = cardRects;
    cardMotionRectsDirtyRef.current = false;

    return cardRects;
  }, [scrollerRef]);

  const resetCardMotion = useCallback(() => {
    if (cardMotionFrameRef.current !== null) {
      window.cancelAnimationFrame(cardMotionFrameRef.current);
      cardMotionFrameRef.current = null;
    }

    cardMotionPreviousTimeRef.current = null;
    invalidateCardMotionRects();

    for (const element of cardMotionStatesRef.current.keys()) {
      element.style.transform = "";
      element.style.willChange = "";
    }

    cardMotionStatesRef.current.clear();
  }, [invalidateCardMotionRects]);

  const animateCardMotion = useCallback((timestamp: number) => {
    const previousTime = cardMotionPreviousTimeRef.current ?? timestamp;
    const deltaSeconds = Math.min((timestamp - previousTime) / 1000, 0.032);
    const damping = Math.exp(-CARD_MOTION_DAMPING * deltaSeconds);
    let hasActiveMotion = false;

    cardMotionPreviousTimeRef.current = timestamp;

    for (const [element, state] of cardMotionStatesRef.current) {
      if (!element.isConnected) {
        cardMotionStatesRef.current.delete(element);
        continue;
      }

      state.rotationVelocity += -state.rotation * CARD_MOTION_ROTATION_SPRING * deltaSeconds;
      state.velocityX += -state.x * CARD_MOTION_SPRING * deltaSeconds;
      state.velocityY += -state.y * CARD_MOTION_SPRING * deltaSeconds;
      state.rotationVelocity *= damping;
      state.velocityX *= damping;
      state.velocityY *= damping;
      state.rotation += state.rotationVelocity * deltaSeconds;
      state.x += state.velocityX * deltaSeconds;
      state.y += state.velocityY * deltaSeconds;

      const isMoving =
        Math.abs(state.rotation) > CARD_MOTION_MINIMUM_ACTIVITY ||
        Math.abs(state.rotationVelocity) > CARD_MOTION_MINIMUM_ACTIVITY ||
        Math.abs(state.x) > CARD_MOTION_MINIMUM_ACTIVITY ||
        Math.abs(state.y) > CARD_MOTION_MINIMUM_ACTIVITY ||
        Math.abs(state.velocityX) > CARD_MOTION_MINIMUM_ACTIVITY ||
        Math.abs(state.velocityY) > CARD_MOTION_MINIMUM_ACTIVITY;

      if (!isMoving) {
        state.rotation = 0;
        state.rotationVelocity = 0;
        state.x = 0;
        state.y = 0;
        state.velocityX = 0;
        state.velocityY = 0;
        element.style.transform = "";
        element.style.willChange = "";
        continue;
      }

      hasActiveMotion = true;
      element.style.transform =
        `translate3d(${state.x.toFixed(3)}px, ${state.y.toFixed(3)}px, 0) ` + `rotate(${state.rotation.toFixed(3)}deg)`;
    }

    if (hasActiveMotion) {
      cardMotionFrameRef.current = window.requestAnimationFrame(animateCardMotion);
      return;
    }

    cardMotionFrameRef.current = null;
    cardMotionPreviousTimeRef.current = null;
  }, []);

  const scheduleCardMotion = useCallback(() => {
    if (cardMotionFrameRef.current !== null || prefersReducedMotionRef.current || !isLargeOrAbove) {
      return;
    }

    cardMotionFrameRef.current = window.requestAnimationFrame(animateCardMotion);
  }, [animateCardMotion, isLargeOrAbove]);

  const applyPointerPush = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "touch" || prefersReducedMotionRef.current || !isLargeOrAbove) {
        return;
      }

      const now = performance.now();
      const lastPointer = lastPointerRef.current;

      if (!lastPointer.hasValue) {
        lastPointerRef.current = {
          hasValue: true,
          time: now,
          x: event.clientX,
          y: event.clientY,
        };
        return;
      }

      const elapsedMilliseconds = Math.max(now - lastPointer.time, 8);
      const pointerVelocityX = clamp(
        ((event.clientX - lastPointer.x) / elapsedMilliseconds) * 1000,
        -CARD_MOTION_VELOCITY_LIMIT,
        CARD_MOTION_VELOCITY_LIMIT,
      );
      const pointerVelocityY = clamp(
        ((event.clientY - lastPointer.y) / elapsedMilliseconds) * 1000,
        -CARD_MOTION_VELOCITY_LIMIT,
        CARD_MOTION_VELOCITY_LIMIT,
      );
      const pointerSpeed = Math.hypot(pointerVelocityX, pointerVelocityY);
      const velocityBoost = calculateVelocityBoost(pointerSpeed);
      const cardRects = getCardMotionRects();

      lastPointerRef.current = {
        hasValue: true,
        time: now,
        x: event.clientX,
        y: event.clientY,
      };

      if (!cardRects.length) {
        return;
      }

      for (const { element, rect } of cardRects) {
        const state = getCardMotionState(cardMotionStatesRef.current, element);
        const influence = calculateCardInfluence(event.clientX, event.clientY, rect);
        const torque = calculateCardTorque(event.clientX, event.clientY, pointerVelocityX, pointerVelocityY, rect);

        element.style.willChange = "transform";
        state.rotationVelocity +=
          torque * pointerSpeed * CARD_MOTION_ROTATION_PUSH_STRENGTH * velocityBoost * influence;
        state.velocityX += pointerVelocityX * CARD_MOTION_PUSH_STRENGTH * velocityBoost * influence;
        state.velocityY += pointerVelocityY * CARD_MOTION_PUSH_STRENGTH * velocityBoost * influence;
      }

      scheduleCardMotion();
    },
    [getCardMotionRects, isLargeOrAbove, scheduleCardMotion],
  );

  const resetPointerTracking = useCallback(() => {
    lastPointerRef.current.hasValue = false;
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const syncReducedMotionPreference = () => {
      prefersReducedMotionRef.current = mediaQuery.matches;

      if (mediaQuery.matches) {
        resetCardMotion();
      }
    };

    syncReducedMotionPreference();
    mediaQuery.addEventListener("change", syncReducedMotionPreference);

    return () => {
      mediaQuery.removeEventListener("change", syncReducedMotionPreference);
      resetCardMotion();
    };
  }, [resetCardMotion]);

  useEffect(() => {
    if (isLargeOrAbove) {
      return;
    }

    resetPointerTracking();
    resetCardMotion();
  }, [isLargeOrAbove, resetCardMotion, resetPointerTracking]);

  useEffect(() => {
    window.addEventListener("resize", invalidateCardMotionRects);
    window.addEventListener("scroll", invalidateCardMotionRects, true);

    return () => {
      window.removeEventListener("resize", invalidateCardMotionRects);
      window.removeEventListener("scroll", invalidateCardMotionRects, true);
    };
  }, [invalidateCardMotionRects]);

  return {
    applyPointerPush,
    resetCardMotion,
    resetPointerTracking,
  };
}

function calculateCardInfluence(pointerX: number, pointerY: number, rect: DOMRect) {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const distance = Math.hypot(pointerX - centerX, pointerY - centerY);
  const radius = Math.max(rect.width, rect.height) * CARD_MOTION_INFLUENCE_RADIUS;

  return 1 / (1 + (distance / radius) ** 2);
}

function calculateCardTorque(
  pointerX: number,
  pointerY: number,
  pointerVelocityX: number,
  pointerVelocityY: number,
  rect: DOMRect,
) {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const normalizedX = clamp((pointerX - centerX) / (rect.width / 2), -1, 1);
  const normalizedY = clamp((pointerY - centerY) / (rect.height / 2), -1, 1);
  const pointerSpeed = Math.hypot(pointerVelocityX, pointerVelocityY);

  if (pointerSpeed <= CARD_MOTION_MINIMUM_ACTIVITY) {
    return 0;
  }

  const normalizedVelocityX = pointerVelocityX / pointerSpeed;
  const normalizedVelocityY = pointerVelocityY / pointerSpeed;

  return normalizedX * normalizedVelocityY - normalizedY * normalizedVelocityX;
}

function calculateVelocityBoost(pointerSpeed: number) {
  const lowSpeedProgress = clamp(pointerSpeed / CARD_MOTION_VELOCITY_BOOST_START, 0, 1);
  const baseVelocityScale =
    CARD_MOTION_MIN_VELOCITY_SCALE +
    lowSpeedProgress ** CARD_MOTION_LOW_SPEED_POWER * (1 - CARD_MOTION_MIN_VELOCITY_SCALE);
  const normalizedSpeed = clamp(
    (pointerSpeed - CARD_MOTION_VELOCITY_BOOST_START) / CARD_MOTION_VELOCITY_BOOST_RANGE,
    0,
    1,
  );
  const gradualBoost = normalizedSpeed ** 1.4;

  return baseVelocityScale + gradualBoost * (CARD_MOTION_MAX_VELOCITY_BOOST - 1);
}

function getCardMotionState(cardMotionStates: Map<HTMLElement, CardMotionState>, element: HTMLElement) {
  const existingState = cardMotionStates.get(element);

  if (existingState) {
    return existingState;
  }

  const state = {
    rotation: 0,
    rotationVelocity: 0,
    velocityX: 0,
    velocityY: 0,
    x: 0,
    y: 0,
  };

  cardMotionStates.set(element, state);

  return state;
}
